import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { WithCustomerPortalApi } from 'core/customerPortalApi';
import { type FileUploadObj } from 'component-library/components/Input/FileUpload';
import { useSignedUrl } from './useSignedUrl';

type UseFileUploadProps = {
  onMutate: (fileObj: FileUploadObj) => void;
  onSuccess: (data: string, fileObj: FileUploadObj) => void;
  onError: (error: unknown, fileObj: FileUploadObj) => void;
};

export const useFileUpload = WithCustomerPortalApi<
  string,
  UseMutationResult<string, undefined, FileUploadObj>,
  UseFileUploadProps
>(
  (fetchFn, { onMutate, onSuccess, onError }) => {
    const { mutateAsync: getSignedUrl } = useSignedUrl();

    return useMutation<string, undefined, FileUploadObj>({
      mutationFn: async ({ file, id }) => {
        const signedUrl = await getSignedUrl({ fileName: file.name, id });

        return fetchFn({
          url: signedUrl,
          excludeHeaders: ['authorization'],
          type: 'text',
          options: {
            method: 'PUT',
            headers: {
              'Content-Type': file.type,
            },
            body: file,
          },
        });
      },
      onMutate,
      onSuccess,
      onError,
    });
  },
  { hookName: 'useFileUpload' },
);
